import { Column, Entity, JoinColumn, ManyToOne, OneToMany } from 'typeorm';
import { DimensionUnit, UnitOfMeasure } from '../../enums';
import { BaseEntity } from './base.entity';
import { LocationItemEntity } from './location-item.entity';
import { MerchandiserEntity } from './merchandiser.entity';
import { WarehouseEntity } from './warehouse.entity';

export enum LocationType {
	MERCHANDISER = 'merchandiser',
	WAREHOUSE_LOCATION = 'warehouse_location',
	VEHICLE = 'vehicle',
}

export enum LocationStatus {
	ACTIVE = 'active',
	INACTIVE = 'inactive',
	MAINTENANCE = 'maintenance',
	OFFLINE = 'offline',
}

/**
 * Sub-type for warehouse locations that distinguishes pickable
 * (sellable) bins from special-purpose bins. NULL is treated as
 * `NORMAL` for backward compat — every existing location is a
 * pickable bin until tagged otherwise.
 *
 * - `NORMAL` — standard pickable bin (default; sales-pick / ATP /
 *   cart-availability queries see stock here)
 * - `QUARANTINE` — damaged-on-arrival receiving holds inventory here
 *   pending vendor-claim resolution. NOT pickable for sales orders.
 * - `STAGING` — inbound dock or pre-putaway holding bin (future use,
 *   when we split receive → putaway into two steps)
 * - `RETURNS` — customer returns holding bin. Pre-trip / post-trip
 *   inspection exceptions of reason='returned' (driver brought back
 *   units that the customer refused) TRANSFER into the per-warehouse
 *   returns bin. See `docs/claude/distribution-refactor/inspection-exceptions.md`.
 * - `MORGUE` — damaged-goods holding bin used by the pre-trip /
 *   post-trip inspection exception flow. Units found damaged on
 *   the truck TRANSFER into the per-warehouse morgue bin pending
 *   a separate write-off / RTV workflow. Distinct from QUARANTINE
 *   (which holds vendor-damaged units at receiving).
 * - `SHRINKAGE` — placeholder bin for units that were expected on
 *   the truck per the load plan but couldn't be found at inspection
 *   time. Mirrors the morgue pattern but with a separate audit
 *   trail so accounting can distinguish "physically broken" from
 *   "unaccounted for" (shrinkage). The bin doesn't hold real
 *   physical units — it's a virtual destination for accounting.
 *
 * Sellable-stock queries must filter `subType IS NULL OR subType = NORMAL`.
 * Audit and update those queries when this column is consumed —
 * tracked in the PO Receipts plan Phase 10.
 */
export enum LocationSubType {
	NORMAL = 'normal',
	QUARANTINE = 'quarantine',
	STAGING = 'staging',
	RETURNS = 'returns',
	MORGUE = 'morgue',
	SHRINKAGE = 'shrinkage',
}

@Entity('location')
export class LocationEntity extends BaseEntity {
	@Column('varchar', { name: 'description', nullable: true, length: 255 })
	public description!: string | null;

	@Column('varchar', { name: 'code', nullable: true, length: 64 })
	public code!: string | null;

	@Column('enum', {
		name: 'type',
		enum: LocationType,
		nullable: true,
	})
	public type!: LocationType | null;

	// PD-13925: a merchandiser's placement (the customer address it sits at)
	// lives on the MerchandiserEntity now, not the location. The old
	// location.fk_client_address__id column is dropped by
	// 1780884906333-DropLocationClientAddressColumn; merchandiser-location
	// placement is read via fkMerchandiser -> fkClientAddress.

	@Column('int', {
		name: 'fk_warehouse__id',
		unsigned: true,
		nullable: true,
	})
	public warehouseId!: number | null;

	@Column('varchar', { name: 'locationCode', nullable: true, length: 64 })
	public locationCode!: string | null;

	/**
	 * PD-13767 — the vehicle that owns this location, when this is a
	 * vehicle's bin. Vehicles are now a real entity (VehicleEntity) that
	 * owns one or more child locations; this is the back-reference. Null for
	 * warehouse/merchandiser locations. The owning vehicle's designated
	 * primary location (vehicle.fk_location__id_primary) is where truck
	 * inventory operations resolve to.
	 */
	@Column('int', { name: 'fk_vehicle__id', unsigned: true, nullable: true })
	public vehicleId!: number | null;

	/**
	 * PD-13921 — the merchandiser that owns this location, when this is a
	 * merchandiser's bin. Merchandisers are now a real entity
	 * (MerchandiserEntity) that owns one or more child locations; this is the
	 * back-reference. Null for warehouse/vehicle locations. A merchandiser that
	 * holds multiple products has one child location per product, each pointing
	 * back here, and inventory is rolled up across them (PD-13922). Every
	 * location has exactly one parent — a warehouse, a vehicle, or a
	 * merchandiser.
	 */
	@Column('int', {
		name: 'fk_merchandiser__id',
		unsigned: true,
		nullable: true,
	})
	public merchandiserId!: number | null;

	@Column('int', { name: 'capacity', nullable: true })
	public capacity!: number | null;

	@Column('enum', {
		name: 'units',
		enum: UnitOfMeasure,
		nullable: true,
	})
	public units!: UnitOfMeasure | null;

	@Column('enum', {
		name: 'status',
		enum: LocationStatus,
		nullable: true,
	})
	public status!: LocationStatus | null;

	/**
	 * Pickability sub-classification for `WAREHOUSE_LOCATION` bins.
	 * NULL = NORMAL (sellable). Set to QUARANTINE for the per-warehouse
	 * damaged-receipts holding bin (one seeded per warehouse via the
	 * Phase 10 quarantine-bin migration).
	 */
	@Column('enum', {
		name: 'subType',
		enum: LocationSubType,
		nullable: true,
	})
	public subType!: LocationSubType | null;

	@Column('decimal', {
		name: 'width',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public width!: string | null;

	@Column('decimal', {
		name: 'height',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public height!: string | null;

	@Column('decimal', {
		name: 'depth',
		precision: 8,
		scale: 2,
		nullable: true,
	})
	public depth!: string | null;

	@Column('enum', {
		name: 'dimensionUnit',
		enum: DimensionUnit,
		nullable: true,
	})
	public dimensionUnit!: DimensionUnit | null;

	// PD-13913: live GPS no longer lives on the location. The truck's last-known
	// position is stored on the VEHICLE (and the acting user); see
	// VehicleEntity/UserEntity currentLatitude/Longitude/At/Source. The legacy
	// location GPS columns are dropped by 1780724999312-DropLocationGpsColumns.

	@ManyToOne(() => WarehouseEntity)
	@JoinColumn({ name: 'fk_warehouse__id' })
	public fkWarehouse!: WarehouseEntity;

	// PD-13925: the owning merchandiser (when this is a merchandiser's bin).
	// A merchandiser-location's placement (client address) is read through this
	// relation now that fk_client_address__id is gone from `location`.
	@ManyToOne(() => MerchandiserEntity)
	@JoinColumn({ name: 'fk_merchandiser__id' })
	public fkMerchandiser!: MerchandiserEntity | null;

	@OneToMany(
		() => LocationItemEntity,
		(locationItem) => locationItem.fkLocation,
	)
	public locationItems!: Array<LocationItemEntity>;
}
